import React, { useState, useRef } from "react";
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  UploadSimple, FileCsv, CheckCircle, XCircle, WarningCircle,
  DownloadSimple, ArrowRight, ArrowLeft, Table as TableIcon,
} from "@phosphor-icons/react";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const STATUS_STYLES = {
  valid: { icon: CheckCircle, cls: "text-emerald-400", bg: "bg-emerald-500/10", border: "border-emerald-500/25", label: "Valid" },
  duplicate: { icon: WarningCircle, cls: "text-amber-400", bg: "bg-amber-500/10", border: "border-amber-500/25", label: "Duplicate" },
  error: { icon: XCircle, cls: "text-rose-400", bg: "bg-rose-500/10", border: "border-rose-500/25", label: "Error" },
};

export default function ImportPage() {
  const [phase, setPhase] = useState("select"); // select | preview | done
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  const pickFile = () => inputRef.current?.click();

  const uploadForPreview = async (f) => {
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/import/csv/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      setPhase("preview");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to parse CSV");
    } finally {
      setLoading(false);
    }
  };

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    uploadForPreview(f);
  };

  const confirm = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/import/csv/confirm", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      setPhase("done");
      toast.success(`Imported ${data.created_credentials} credentials (${data.created_staff} new staff)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setLoading(false);
    }
  };

  const resetAll = () => {
    setPhase("select");
    setFile(null);
    setPreview(null);
    setResult(null);
  };

  return (
    <div className="p-4 md:p-8 max-w-[1400px] mx-auto">
      <Link to="/staff" data-testid="import-back-link" className="inline-flex items-center gap-2 text-slate-400 hover:text-slate-200 text-sm mb-6">
        <ArrowLeft size={14} weight="bold" /> Back to Staff
      </Link>

      <div className="mb-8 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="font-label text-orange-400 mb-2">BULK IMPORT</div>
          <h1 className="text-4xl md:text-5xl font-display font-black tracking-tight">CSV Import</h1>
          <p className="text-slate-400 mt-2 max-w-2xl">
            Upload a CSV of staff and credentials in one shot. We validate every row, catch duplicates,
            and never silently import invalid data.
          </p>
        </div>
        <Link
          to="/import/history"
          data-testid="import-history-link"
          className="rounded-full border border-white/10 bg-slate-900 text-slate-100 hover:bg-slate-800 px-5 py-2.5 text-sm font-semibold inline-flex items-center gap-2"
        >
          View import history <ArrowRight size={14} weight="bold" />
        </Link>
      </div>

      {/* Phase: SELECT */}
      {phase === "select" && (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
          <div
            data-testid="import-drop-zone"
            onClick={pickFile}
            className="rounded-2xl border-2 border-dashed border-white/15 bg-slate-900 p-10 text-center cursor-pointer hover:border-orange-500/40 transition-colors"
          >
            <input
              ref={inputRef}
              type="file"
              accept=".csv"
              onChange={onFileChange}
              className="hidden"
              data-testid="import-file-input"
            />
            <div className="w-16 h-16 rounded-2xl bg-orange-500/15 border border-orange-500/25 grid place-items-center mx-auto mb-4">
              <UploadSimple size={30} weight="bold" className="text-orange-400" />
            </div>
            <div className="text-xl font-display font-black tracking-tight mb-2">Drop your CSV file here</div>
            <div className="text-slate-500 text-sm mb-6">Or click to browse · Max 5 MB · UTF-8 encoded</div>
            <Button
              data-testid="import-select-file-button"
              type="button"
              className="bg-orange-500 text-white hover:bg-orange-400 font-semibold rounded-full h-11 px-6"
              disabled={loading}
            >
              {loading ? "Parsing…" : "Select CSV File"}
            </Button>
          </div>

          <div className="rounded-2xl border border-white/10 bg-slate-900 p-6">
            <div className="font-label text-slate-500 mb-3">TEMPLATE</div>
            <h3 className="font-display font-black tracking-tight text-lg mb-2">Not sure what columns?</h3>
            <p className="text-slate-400 text-sm mb-4">
              Download our template with example rows. Required columns: first_name, last_name, email, role,
              credential_type, credential_number, issue_date, expiration_date, status.
            </p>
            <a
              href={`${API}/import/csv/template`}
              data-testid="download-template-link"
              className="inline-flex items-center gap-2 rounded-full border border-orange-500/40 bg-orange-500/10 text-orange-300 px-5 py-2.5 text-sm font-semibold hover:bg-orange-500/15 transition-colors"
            >
              <DownloadSimple size={14} weight="bold" /> Download CSV template
            </a>

            <div className="mt-6 pt-6 border-t border-white/10">
              <div className="font-label text-slate-500 mb-2">ROLE VALUES</div>
              <ul className="text-xs text-slate-400 space-y-1">
                <li><code className="text-orange-300">coach</code> · Coach</li>
                <li><code className="text-orange-300">trainer</code> · Athletic Trainer</li>
                <li><code className="text-orange-300">referee</code> · Referee/Official</li>
                <li><code className="text-orange-300">volunteer</code> · Volunteer</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Phase: PREVIEW */}
      {phase === "preview" && preview && (
        <div>
          {/* Summary */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            {["valid", "duplicate", "error"].map((k) => {
              const s = STATUS_STYLES[k];
              return (
                <div key={k} className={`rounded-xl border p-5 ${s.border} ${s.bg}`}>
                  <div className={`font-label ${s.cls}`}>{s.label.toUpperCase()}</div>
                  <div className="kpi-number mt-2" data-testid={`preview-count-${k}`}>{preview.counts[k]}</div>
                  <div className="text-xs text-slate-400 mt-1">out of {preview.total_rows}</div>
                </div>
              );
            })}
          </div>

          {preview.counts.error > 0 && (
            <div className="mb-6 rounded-xl border border-rose-500/30 bg-rose-500/5 p-4 flex items-start gap-3" data-testid="import-error-notice">
              <XCircle size={18} weight="bold" className="text-rose-400 shrink-0 mt-0.5" />
              <div className="text-sm">
                <div className="font-semibold text-rose-300">{preview.counts.error} row{preview.counts.error > 1 ? "s" : ""} contain errors and will NOT be imported.</div>
                <div className="text-slate-400 mt-1">Fix them in your source file and re-upload, or continue to import only the valid rows.</div>
              </div>
            </div>
          )}

          {preview.counts.duplicate > 0 && (
            <div className="mb-6 rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 flex items-start gap-3" data-testid="import-duplicate-notice">
              <WarningCircle size={18} weight="bold" className="text-amber-400 shrink-0 mt-0.5" />
              <div className="text-sm">
                <div className="font-semibold text-amber-300">{preview.counts.duplicate} duplicate row{preview.counts.duplicate > 1 ? "s" : ""} will be skipped.</div>
                <div className="text-slate-400 mt-1">A duplicate has the same staff + credential type + expiration date as an existing record.</div>
              </div>
            </div>
          )}

          {/* Row table */}
          <div className="rounded-2xl border border-white/10 bg-slate-900 overflow-hidden">
            <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <TableIcon size={16} className="text-slate-400" weight="bold" />
                <span className="font-semibold text-slate-200">Row-by-row preview ({preview.rows.length} shown)</span>
              </div>
              <div className="text-xs text-slate-500">Nothing has been saved yet.</div>
            </div>
            <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
              <Table>
                <TableHeader className="sticky top-0 bg-slate-900 z-10">
                  <TableRow className="border-white/5">
                    <TableHead className="text-slate-400 font-label">#</TableHead>
                    <TableHead className="text-slate-400 font-label">Status</TableHead>
                    <TableHead className="text-slate-400 font-label">Name</TableHead>
                    <TableHead className="text-slate-400 font-label">Role</TableHead>
                    <TableHead className="text-slate-400 font-label">Credential</TableHead>
                    <TableHead className="text-slate-400 font-label">Expires</TableHead>
                    <TableHead className="text-slate-400 font-label">Issue</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {preview.rows.map((r, i) => {
                    const s = STATUS_STYLES[r.row_status];
                    const Icon = s.icon;
                    return (
                      <TableRow key={i} data-testid={`preview-row-${r.row}`} className="border-white/5 hover:bg-white/[0.02]">
                        <TableCell className="text-slate-500 text-xs">{r.row}</TableCell>
                        <TableCell>
                          <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold border ${s.border} ${s.bg} ${s.cls}`}>
                            <Icon size={12} weight="bold" /> {s.label}
                          </span>
                          {r.errors?.length > 0 && (
                            <div className="text-xs text-rose-400 mt-1">{r.errors.join("; ")}</div>
                          )}
                          {r.matched_staff_name && r.row_status !== "error" && (
                            <div className="text-xs text-slate-500 mt-1">Matches: {r.matched_staff_name}</div>
                          )}
                        </TableCell>
                        <TableCell className="text-slate-200">{r.first_name} {r.last_name}</TableCell>
                        <TableCell className="text-slate-400 capitalize">{r.role || "—"}</TableCell>
                        <TableCell className="text-slate-300">{r.credential_type || "—"}</TableCell>
                        <TableCell className="text-slate-400 text-sm">{r.expiration_date || "—"}</TableCell>
                        <TableCell className="text-slate-500 text-sm">{r.issue_date || "—"}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </div>

          {/* Nav */}
          <div className="flex items-center justify-between mt-6">
            <Button
              variant="ghost"
              onClick={resetAll}
              className="text-slate-400 hover:text-slate-100"
              data-testid="import-choose-different"
            >
              <ArrowLeft size={14} weight="bold" className="mr-1.5" /> Choose a different file
            </Button>
            <Button
              data-testid="import-confirm-button"
              onClick={confirm}
              disabled={loading || preview.counts.valid === 0}
              className="bg-orange-500 text-white hover:bg-orange-400 font-semibold rounded-full h-11 px-6"
            >
              {loading ? "Importing…" : `Import ${preview.counts.valid} valid row${preview.counts.valid === 1 ? "" : "s"}`}
              <ArrowRight size={14} weight="bold" className="ml-2" />
            </Button>
          </div>
        </div>
      )}

      {/* Phase: DONE */}
      {phase === "done" && result && (
        <div className="rounded-2xl border border-emerald-500/25 bg-emerald-500/5 p-10 text-center" data-testid="import-done-panel">
          <div className="w-16 h-16 rounded-2xl bg-emerald-500/15 border border-emerald-500/25 grid place-items-center mx-auto mb-4">
            <CheckCircle size={32} weight="bold" className="text-emerald-400" />
          </div>
          <h2 className="text-3xl font-display font-black tracking-tight mb-3">Import complete</h2>
          <p className="text-slate-400 mb-8">
            Added <span className="text-slate-100 font-semibold">{result.created_credentials}</span> credential(s) across <span className="text-slate-100 font-semibold">{result.created_staff}</span> new staff member(s).
            {" "}Skipped {result.skipped_error} invalid and {result.skipped_duplicate} duplicate row(s).
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Button
              data-testid="import-goto-staff"
              onClick={() => navigate("/staff")}
              className="bg-orange-500 text-white hover:bg-orange-400 font-semibold rounded-full"
            >
              View Staff
            </Button>
            <Button
              variant="ghost"
              onClick={resetAll}
              className="text-slate-300 hover:text-slate-100"
            >
              Import another file
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
